/**
 * Retentativa do carregamento de chunk lazy
 *
 * ## Por que existe
 *
 * Medição de 12/08/2026, dois dias, devices distintos com `ChunkLoadError` na
 * rota `{loja}/payment`:
 *
 *     iOS in-app (Instagram/Facebook)  472  (57,2%)
 *     iOS Safari/WKWebView             202  (24,5%)
 *     iOS Chrome                       148  (17,9%)
 *     Android                            1  ( 0,1%)
 *
 * **Um device Android em 825.** E os chunks que falham EXISTEM: pedi cada URL
 * da telemetria à borda e todas voltaram `200 text/javascript` com o tamanho
 * certo — inclusive a que sozinha concentra 83% dos erros
 * (`src_app_pages_success_success_module_ts`, a tela de sucesso do pedido).
 * A build também é a corrente, não uma fóssil.
 *
 * Ou seja: não é chunk podado do S3 (a retenção por tempo já cobre 48 h), não é
 * frescor, não é o SPA fallback (`chunk_type='missing'` é ZERO na série). É a
 * requisição morrendo em trânsito num WKWebView — o padrão bate com a volta do
 * provedor de pagamento, quando o webview foi suspenso e retomado. Os campos de
 * `PerformanceResourceTiming` reforçam: 840 de 840 desses eventos não têm
 * NENHUMA entry para a URL do chunk, isto é, a requisição nem chegou a ser
 * registrada.
 *
 * Para uma falha transitória a cura não é recarregar a página — é **pedir de
 * novo**. Recarregar custa o estado inteiro do usuário (que acabou de pagar);
 * retentar custa 250 ms e é invisível.
 *
 * ## Como se enganxa no webpack
 *
 * `__webpack_require__.l(url, pronto, chave, chunkId)` é quem cria o `<script>`
 * do chunk. Envolvê-lo dá retentativa para TUDO que é lazy — rotas do Router e
 * `import()` direto (swiper, fingerprintjs) — sem tocar em nenhum call site.
 *
 * **A armadilha:** `pronto` é chamado no SUCESSO também. O runtime faz
 *
 *     var p = c && ("load" === c.type ? "missing" : c.type)
 *
 * e só then decide se houve erro, olhando se o chunk se registrou. Quem lê o
 * evento de fora NÃO consegue distinguir `load` bem-sucedido de `load` que não
 * registrou. Retentar em `load` retentaria todo chunk bom do app.
 *
 * Por isso a regra é a mais estreita possível: **só `error` e `timeout`**, os
 * dois tipos que o runtime só emite quando o script não carregou. Coincidem com
 * a população medida, e `missing` (onde retry é comprovadamente inútil, porque a
 * resposta errada está cacheada) fica de fora por construção.
 *
 * ## Escalada
 *
 * 1ª retentativa na MESMA URL, 250 ms depois: se o webview só perdeu a conexão
 * no instante, o pedido seguinte pode até sair do cache HTTP.
 * 2ª com `&mp_retry=2`, 1200 ms depois: muda a chave de cache do CloudFront e
 * força origem, cobrindo o caso de uma resposta ruim ter sido guardada. Query
 * string não altera a chave do objeto no S3, então isto nunca aponta para lugar
 * nenhum novo.
 *
 * Custo máximo quando a rede está de fato morta: 1,45 s de espera somada e duas
 * requisições a mais — depois disso o erro sobe exatamente como subia antes.
 *
 * ## Segurança
 *
 * Este código roda ANTES do bootstrap do Angular e no caminho de carga de todo
 * chunk do app. Um defeito aqui não degrada uma tela: derruba o app inteiro. Daí
 * as três regras duras:
 *
 *  - **`pronto` é sempre chamado, exatamente uma vez.** Se ele nunca for
 *    chamado a Promise do chunk fica pendente para sempre e a rota nunca carrega
 *    — falha pior e mais silenciosa que a original.
 *  - **nunca lança.** Qualquer exceção no wrapper viraria erro de carregamento
 *    de chunk, que é justamente o que estamos consertando.
 *  - **degrada para o comportamento de hoje.** Se o runtime não estiver
 *    acessível, `instalarRetryDeChunk()` devolve `false` e nada é alterado.
 */

/** Tipos que o runtime só emite quando o script NÃO carregou (ver cabeçalho). */
const MOTIVOS_RETENTAVEIS = ['error', 'timeout'];

/** Espera antes de cada retentativa, em ms. O tamanho define quantas há. */
export const ESPERAS_MS = [250, 1200];

/** Parâmetro que fura o cache da borda na última tentativa. */
export const PARAM_RETRY = 'mp_retry';

/** Assinatura de `__webpack_require__.l`. */
export type CarregadorDeChunk = (
  url: string,
  pronto: (evento: any) => void,
  chave: string | undefined,
  chunkId: string | number,
) => void;

/**
 * Quantas retentativas a última sequência gastou, por chunk. Indexado pelo
 * NOME DO ARQUIVO (último segmento do caminho, sem query): o nome do chunk já
 * carrega o hash de conteúdo, então a chave identifica chunk e build juntos.
 *
 * Por que o nome e não a URL: o runtime pede o chunk com URL RELATIVA (o build
 * de produção tem `__webpack_require__.p = ""`), mas `err.request` — por onde o
 * diagnóstico pergunta — vem de `script.src`, que o DOM devolve ABSOLUTA. Com a
 * URL inteira como chave, os dois lados nunca se encontravam: medido de 01 a
 * 08/09/2026, 100% dos eventos com o patch chegavam com `retry_n = 0` enquanto
 * 389 devices carregavam `mp_retry=2` na própria URL do erro. A retentativa
 * rodava; a telemetria é que jurava o contrário.
 *
 * Serve ao diagnóstico: um `ChunkLoadError` que sobrevive carrega `retry_n = 2`
 * e prova que o mecanismo rodou. Um erro SEM o campo prova o contrário — que o
 * patch não instalou. Sem esse par a correção seria inauditável no Athena.
 *
 * Guarda a ÚLTIMA sequência, e uma sequência por vez basta: o runtime não chama
 * `l` duas vezes para o mesmo chunk. Quem pede um chunk já em voo cai no
 * `if(inProgress[url])` de dentro do `l` e é enfileirado, e `f.j` nem chega lá
 * quando a Promise do chunk existe. Dois `l` concorrentes para a mesma URL
 * exigiriam dois chunkIds distintos apontando para o mesmo arquivo, o que
 * `__webpack_require__.u` não produz.
 *
 * `Object.create(null)`, não `{}`: a chave vem de uma URL, e um `__proto__`
 * atravessando aqui se comportaria como setter de protótipo em vez de entrada.
 */
const gastas: { [urlBase: string]: number } = Object.create(null);

/** Instalado com sucesso? É o que decide se `retry_n` viaja na telemetria. */
let instalado = false;

/** A URL sem query (o `?release=` e o `&mp_retry=` variam entre tentativas). */
function semQuery(url: string): string {
  const u = String(url || '');
  const corte = u.indexOf('?');
  return corte === -1 ? u : u.slice(0, corte);
}

/**
 * Chave do registro: o nome do arquivo. Iguala `src_app_x.abc.js?release=1` (o
 * que o runtime pede) a `https://pedir.delivery/app/src_app_x.abc.js?release=1&mp_retry=2`
 * (o que `err.request` traz de volta). Ver o comentário de `gastas`.
 */
export function chaveDoChunk(url: string): string {
  const semQ = semQuery(url);
  const barra = semQ.lastIndexOf('/');
  return barra === -1 ? semQ : semQ.slice(barra + 1);
}

/** O patch está no ar? Distingue "retentou e não adiantou" de "não instalou". */
export function retryInstalado(): boolean {
  return instalado;
}

/**
 * Retentativas gastas na última sequência deste chunk. `null` quando o patch
 * não está instalado — para o campo ficar AUSENTE no payload em vez de mentir
 * um zero que se confundiria com "instalado e não precisou retentar".
 */
export function retentativasDe(url: string): number | null {
  if (!instalado) return null;
  const n = gastas[chaveDoChunk(url)];
  return typeof n === 'number' ? n : 0;
}

/**
 * Acrescenta o marcador de retentativa à URL. Preserva o `?release=` que o
 * `cache_bust_script.sh` embute em `__webpack_require__.u`.
 *
 * A 1ª retentativa vai na MESMA URL de propósito: se o webview só perdeu a
 * conexão no instante, o pedido seguinte pode ser servido pelo cache HTTP sem
 * tocar a rede. Só da 2ª em diante o marcador entra, para furar uma resposta
 * ruim eventualmente guardada na borda.
 */
export function urlDaRetentativa(url: string, tentativa: number): string {
  const u = String(url || '');
  if (tentativa < 2) return u;
  return u + (u.indexOf('?') === -1 ? '?' : '&') + PARAM_RETRY + '=' + tentativa;
}

/** O evento merece retentativa? Só `error`/`timeout` — nunca `load`. */
export function motivoRetentavel(evento: any): string | null {
  try {
    const tipo = evento && evento.type;
    if (typeof tipo !== 'string') return null;
    return MOTIVOS_RETENTAVEIS.indexOf(tipo) === -1 ? null : tipo;
  } catch (e) {
    return null; // evento exótico (getter que lança): trata como não-retentável
  }
}

/**
 * Envolve um carregador de chunk com retentativa. Puro de propósito: recebe o
 * carregador e o agendador, então o teste exercita a máquina de estados inteira
 * sem webpack, sem DOM e sem relógio.
 */
export function comRetentativa(
  original: CarregadorDeChunk,
  deps: {
    agendar?: (fn: () => void, ms: number) => void;
    esperas?: number[];
    aoRetentar?: (info: { chunkId: string; url: string; tentativa: number; motivo: string }) => void;
  } = {},
): CarregadorDeChunk {
  const agendar = deps.agendar || ((fn: () => void, ms: number) => { setTimeout(fn, ms); });
  const esperas = deps.esperas || ESPERAS_MS;

  return (url, pronto, chave, chunkId) => {
    const base = chaveDoChunk(url);
    let tentativa = 0;

    const tentar = (urlAtual: string) => {
      original(urlAtual, (evento: any) => {
        let deRetentar = false;
        try {
          const motivo = motivoRetentavel(evento);
          if (motivo && tentativa < esperas.length) {
            const espera = esperas[tentativa];
            tentativa++;
            gastas[base] = tentativa;
            const proxima = urlDaRetentativa(url, tentativa);
            if (deps.aoRetentar) {
              try {
                deps.aoRetentar({ chunkId: String(chunkId), url: proxima, tentativa, motivo });
              } catch (e) { /* observador não pode cancelar a retentativa */ }
            }
            // Dois caminhos de falha, e a diferença é o TEMPO, não o lugar:
            //  - o que lançar AGORA (inclusive o próprio `agendar`) ainda está
            //    dentro do try externo, que reverte `deRetentar` e deixa o
            //    bloco no fim entregar o evento;
            //  - o que lançar DEPOIS, quando o timer disparar, já não tem esse
            //    bloco pela frente — por isso o catch de dentro do `agendar`
            //    chama `pronto` ele mesmo.
            // Nos dois `pronto` sai exatamente uma vez, que é o invariante.
            deRetentar = true;
            agendar(() => {
              try {
                tentar(proxima);
              } catch (e) {
                pronto(evento); // o agendamento falhou: devolve a falha original
              }
            }, espera);
          }
        } catch (e) {
          deRetentar = false; // qualquer imprevisto: comportamento de hoje
        }
        if (!deRetentar) {
          if (tentativa === 0) gastas[base] = 0;
          pronto(evento);
        }
      }, chave, chunkId);
    };

    tentar(url);
  };
}

/**
 * Referência ao runtime do webpack. Dentro de um módulo compilado por webpack
 * este identificador resolve para o parâmetro do wrapper do módulo. Fora dele
 * (SSR, teste unitário, build que troque de bundler) simplesmente não existe —
 * e por isso a checagem abaixo é `typeof`, a única forma segura de perguntar
 * por um identificador que pode não ter sido declarado.
 */
declare const __webpack_require__: any;

/**
 * Instala a retentativa. Idempotente e silencioso: devolve `false` — sem alterar
 * nada — quando o runtime não está ao alcance.
 *
 * `runtime` existe para o teste. O padrão é o runtime de verdade, e o spec NÃO
 * pode usá-lo: patchear o `__webpack_require__` do Karma valeria para a suíte
 * inteira e contaminaria o carregamento de chunk dos outros testes. Que o
 * identificador resolve mesmo está provado onde importa — no bundle de produção,
 * onde o compilador o liga ao 3º parâmetro do wrapper do módulo (`const he = n`)
 * e até elimina o `typeof` por saber que existe.
 */
export function instalarRetryDeChunk(
  deps: Parameters<typeof comRetentativa>[1] = {},
  runtime: any = (typeof __webpack_require__ === 'undefined' ? null : __webpack_require__),
): boolean {
  try {
    if (!runtime || typeof runtime.l !== 'function') return false;
    if (runtime.__mpRetryInstalado) return false;

    const original: CarregadorDeChunk = runtime.l.bind(runtime);
    runtime.l = comRetentativa(original, deps);
    runtime.__mpRetryInstalado = true;
    instalado = true;
    return true;
  } catch (e) {
    return false; // runtime congelado/exótico: segue sem retentativa
  }
}

/** Só para teste: devolve o módulo ao estado de antes de instalar. */
export function _resetarRetryParaTeste(): void {
  instalado = false;
  for (const k of Object.keys(gastas)) delete gastas[k];
}
